// Thin compatibility shim over Better Auth's client SDK. Existing
// components keep calling `authService.login(...)` etc.; under the
// hood every call delegates to better-auth/react. There is no
// localStorage, no JWT visible to JavaScript — Better Auth's HttpOnly
// session cookie is the single source of truth, and FastAPI reads the
// same cookie out of Postgres' "session" table to identify the user.

import { authClient } from '@/lib/auth/client'

export type UserRole = 'user' | 'admin' | 'org_admin'

export interface UserInfo {
  id: number
  email: string
  username: string
  full_name: string
  role: UserRole
  is_verified: boolean
  is_active: boolean
}

/** True when the current user can use admin-only surfaces (invitations,
 *  org-wide management). org_admin counts so a workspace owner can
 *  manage their own org without being a platform-wide admin. */
export function isAdmin(user: { role?: string } | null | undefined): boolean {
  return user?.role === 'admin' || user?.role === 'org_admin'
}

export const AUTH_DISABLED = process.env.NEXT_PUBLIC_DISABLE_AUTH === 'true'

// Bridge: Better Auth's user object uses string ids + camelCase fields,
// but our existing UI/types expect the FastAPI shape. The catch-all
// backend proxy passes the session cookie through to FastAPI, which
// still owns the canonical UserInfo schema — we hit /api/users/me for
// the rich profile.
async function fetchUserProfile(): Promise<UserInfo> {
  const res = await fetch('/api/users/me', { credentials: 'same-origin' })
  if (!res.ok) {
    if (res.status === 401) throw new Error('Not authenticated')
    throw new Error('Failed to get user info')
  }
  return res.json()
}

export class AuthService {
  private static instance: AuthService

  private constructor() {}

  static getInstance(): AuthService {
    if (!AuthService.instance) AuthService.instance = new AuthService()
    return AuthService.instance
  }

  async login(email: string, password: string): Promise<void> {
    const { error } = await authClient.signIn.email({ email, password })
    if (error) throw new Error(error.message || 'Login failed')
  }

  async register(data: {
    email: string
    username: string
    password: string
    full_name: string
    invitation_token?: string
  }): Promise<{ requires_verification: boolean }> {
    // Better Auth's /sign-up/email accepts additionalFields configured
    // in server.ts (username, invitation_token). The before-hook there
    // validates the invitation against the durable invitations table —
    // a missing or wrong token throws BAD_REQUEST with a usable detail.
    //
    // `autoSignIn` is off + `requireEmailVerification` is on, so a
    // successful sign-up does NOT log the user in. Better Auth dispatches
    // the verification email via the configured `sendVerificationEmail`
    // callback; the user clicks the link, the account is verified, then
    // they can sign in.
    // Better Auth's `user` (singular) table doesn't carry a username
    // column, so we don't pass `username` here — it would fail body
    // validation. The legacy `users` (plural) table is populated by
    // server.ts's after-hook with email + name + role from the
    // invitation; `username` is currently dropped on the floor.
    const result = await authClient.signUp.email({
      email: data.email,
      password: data.password,
      name: data.full_name,
      invitation_token: data.invitation_token ?? '',
    } as Parameters<typeof authClient.signUp.email>[0])

    if (result.error) {
      throw new Error(result.error.message || 'Registration failed')
    }
    return { requires_verification: true }
  }

  async getCurrentUser(): Promise<UserInfo> {
    if (AUTH_DISABLED) {
      return {
        id: 0,
        email: 'dev@local',
        username: 'dev',
        full_name: 'Dev User',
        role: 'admin',
        is_verified: true,
        is_active: true,
      }
    }
    return fetchUserProfile()
  }

  async refreshAccessToken(): Promise<boolean> {
    // Better Auth rotates sessions automatically on use (updateAge).
    return true
  }

  async logout(): Promise<void> {
    await authClient.signOut().catch(() => {})
  }

  async authenticatedFetch(url: string, options: RequestInit = {}): Promise<Response> {
    return fetch(url, {
      ...options,
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', ...(options.headers || {}) },
    })
  }

  async isAuthenticated(): Promise<boolean> {
    if (AUTH_DISABLED) return true
    try {
      await this.getCurrentUser()
      return true
    } catch {
      return false
    }
  }
}

export const authService = AuthService.getInstance()
